import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Phone } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Reveal } from "./Reveal";
import { Seo } from "./Seo";
import { SERVICES, SERVICE_LINKS, SITE_URL, pageJsonLd } from "@/data/prestations";
import { scrollToId } from "@/lib/scroll";

export const useContactLink = () => {
  const navigate = useNavigate();
  return (e) => {
    e.preventDefault();
    if (window.location.pathname === "/") {
      scrollToId("#contact");
    } else {
      navigate("/#contact");
    }
  };
};

export const ServicePage = ({ slug }) => {
  const page = SERVICES[slug];
  const goContact = useContactLink();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) return null;

  const canonical = `${SITE_URL}/${page.slug}`;
  const others = SERVICE_LINKS.filter((l) => l.slug !== page.slug);
  const jsonLd = pageJsonLd(page);

  return (
    <div className="min-h-screen bg-casse font-body text-anthracite" data-testid={`page-${page.slug}`}>
      <Seo title={page.title} description={page.meta} canonical={canonical} jsonLd={jsonLd} />
      <Header />
      <main className="pt-32 lg:pt-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <nav aria-label="Fil d'Ariane" data-testid="breadcrumb" className="mb-10 flex items-center gap-2 font-accent text-xs tracking-[0.14em] uppercase text-anthracite/50">
            <Link to="/" data-testid="breadcrumb-home" className="hover:text-nuit transition-colors duration-300">Accueil</Link>
            <ChevronRight size={12} strokeWidth={1.5} aria-hidden="true" />
            <span className="text-nuit" aria-current="page">{page.breadcrumb}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-14 lg:gap-16 items-start">
            <Reveal className="lg:col-span-7">
              <h1 className="font-heading font-semibold text-nuit tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl" data-testid="service-h1">
                {page.h1}
              </h1>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-anthracite/75" data-testid="service-intro">
                {page.intro}
              </p>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={0.15}>
              <figure>
                <div className="overflow-hidden aspect-[4/3]">
                  <img src={page.image} alt={page.imageAlt} className="h-full w-full object-cover" data-testid="service-image" />
                </div>
                <figcaption className="mt-3 font-accent text-[11px] tracking-[0.24em] uppercase text-anthracite/50">
                  {page.imageCaption}
                </figcaption>
              </figure>
            </Reveal>
          </div>

          <div className="mt-20 lg:mt-28 max-w-3xl">
            {page.sections.map((s, i) => (
              <Reveal key={s.heading} className={i > 0 ? "mt-16" : ""}>
                <section data-testid={`service-section-${i + 1}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="h-px w-10 bg-sable" aria-hidden="true" />
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-nuit">{s.heading}</h2>
                  </div>
                  {s.paragraphs.map((p, j) => (
                    <p key={j} className="mt-4 text-base leading-relaxed text-anthracite/75">{p}</p>
                  ))}
                  {s.bullets && (
                    <ul className="mt-6 space-y-3">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-base text-anthracite/80">
                          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-sable-deep" aria-hidden="true" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </Reveal>
            ))}
          </div>

          {page.faq.length > 0 && (
            <Reveal className="mt-20 max-w-3xl">
              <div className="flex items-center gap-4 mb-8">
                <span className="h-px w-10 bg-sable" aria-hidden="true" />
                <h2 className="font-heading text-2xl font-semibold tracking-tight text-nuit">Questions fréquentes</h2>
              </div>
              <div className="border-b border-mineral" data-testid="service-faq">
                {page.faq.map((f, i) => (
                  <details key={f.q} className="group border-t border-mineral" data-testid={`faq-item-${i + 1}`}>
                    <summary className="flex cursor-pointer items-center justify-between gap-6 py-5 font-accent text-base font-medium text-nuit list-none [&::-webkit-details-marker]:hidden">
                      {f.q}
                      <span className="shrink-0 text-sable-deep transition-transform duration-300 group-open:rotate-45 text-xl leading-none" aria-hidden="true">+</span>
                    </summary>
                    <p className="pb-6 pr-10 text-sm leading-relaxed text-anthracite/70">{f.a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        <Reveal className="mt-20 lg:mt-28">
          <div className="bg-nuit" data-testid="service-cta">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 lg:py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight text-casse">Parlons de votre projet</h2>
                <p className="mt-3 text-sm leading-relaxed text-casse/70 max-w-xl">
                  Décrivez votre besoin, joignez vos plans ou photos : nous vous rappelons en moins de 48 h pour organiser un premier échange.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0">
                <a
                  href="/#contact"
                  onClick={goContact}
                  data-testid="service-cta-button"
                  className="bg-sable px-8 py-4 text-center font-accent text-sm font-medium tracking-[0.12em] uppercase text-nuit hover:bg-casse transition-colors duration-300"
                >
                  {page.ctaLabel || "Demander une étude"}
                </a>
                <a
                  href="tel:+33786256965"
                  data-testid="service-cta-phone"
                  className="inline-flex items-center justify-center gap-2 border border-casse/30 px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-casse hover:border-sable hover:text-sable transition-colors duration-300"
                >
                  <Phone size={15} strokeWidth={1.5} aria-hidden="true" />
                  07 86 25 69 65
                </a>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
          <Reveal>
            <h2 className="font-accent text-xs font-medium tracking-[0.3em] uppercase text-anthracite/50">Découvrir aussi</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-mineral border border-mineral" data-testid="service-related">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/${o.slug}`}
                  data-testid={`related-${o.slug}`}
                  className="group bg-white p-6 flex items-center justify-between gap-4 transition-colors duration-300 hover:bg-casse"
                >
                  <span className="font-accent text-sm font-medium text-nuit">{o.label}</span>
                  <ArrowRight size={16} strokeWidth={1.5} className="shrink-0 text-sable-deep transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const NotFound = () => (
  <div className="min-h-screen bg-casse font-body text-anthracite flex flex-col" data-testid="not-found-page">
    <Seo title="Page introuvable — LIMEN" description="La page demandée n'existe pas." canonical={`${SITE_URL}/404`} />
    <Header />
    <main className="flex-1 flex items-center pt-32 pb-24">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <p className="font-heading text-6xl font-semibold text-sable-deep/60">404</p>
        <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight text-nuit">Cette page n'existe pas</h1>
        <p className="mt-4 text-base text-anthracite/70">
          Le lien que vous avez suivi est peut-être ancien ou erroné. Retournez à l'accueil ou décrivez-nous directement votre projet.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/" data-testid="notfound-home-link" className="bg-nuit px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-casse hover:bg-anthracite transition-colors duration-300">
            Retour à l'accueil
          </Link>
          <Link to="/#contact" data-testid="notfound-contact-link" className="border border-nuit/25 px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-nuit hover:border-sable-deep hover:text-sable-deep transition-colors duration-300">
            Demander une étude
          </Link>
        </div>
      </div>
    </main>
    <Footer />
  </div>
);
